import React, { useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import moment from "moment-timezone";
import { useForm, Controller } from "react-hook-form";

import ModalIndex from "@Modals/layout/index";
import TextareaForm from "@components/textareaForm/TextareaSimple";

import { SurveyObjective } from "@services/all.services";

import NotationPreview from "./NotationPreview";

export default function ModalUpsertObjective(props) {
  const { isOpen, onClickClose, selectedSubject, respondent, readOnly } = props;
  const { t: translator } = useTranslation("assesment.frequency");
  const { handleSubmit, control, reset } = useForm();

  const selectedObjective = useMemo(() => {
    return selectedSubject?.surveySubjectNotation?.SurveyObjective;
  }, [selectedSubject]);

  useEffect(() => {
    if (isOpen) {
      if (selectedObjective) {
        reset({
          nextObjective: selectedObjective?.nextObjective || "",
          availableMeans: selectedObjective?.availableMeans || "",
          mainObjective: selectedObjective?.mainObjective || ""
        });
      } else {
        reset({
          nextObjective: "",
          availableMeans: "",
          mainObjective: ""
        });
      }
    }
  }, [selectedObjective, isOpen]);

  const [isSaving, setSaving] = useState(false);
  async function onSuccess(formValue) {
    setSaving(true);
    const hasChanges = checkChanges(formValue);

    if (hasChanges) {
      if (selectedObjective) {
        await SurveyObjective.updateById({
          _id: selectedObjective._id,
          ...formValue,
          lastUpdate: moment().format("YYYY-MM-DD"),
          lastUpdateHour: moment().format("HH:mm:ss"),
          SurveySubject: selectedSubject._id,
          Enterprise: localStorage.getItem("enterpriseId")
        });
      } else {
        await SurveyObjective.insert({
          ...formValue,
          respondent,
          lastUpdate: moment().format("YYYY-MM-DD"),
          lastUpdateHour: moment().format("HH:mm:ss"),
          SurveySubject: selectedSubject._id,
          Enterprise: localStorage.getItem("enterpriseId")
        });
      }
    }

    setSaving(false);
    onClickClose();
  }

  function checkChanges(formValue) {
    const lastObjective = selectedObjective;

    if (!lastObjective) {
      return true;
    }

    return (
      lastObjective?.nextObjective !== formValue.nextObjective ||
      lastObjective?.availableMeans !== formValue.availableMeans ||
      lastObjective?.mainObjective !== formValue.mainObjective
    );
  }

  if (!selectedSubject) {
    return null;
  }

  return (
    <ModalIndex
      isOpen={isOpen}
      onClickClose={onClickClose}
      buttonType={readOnly ? "none" : "double"}
      buttonOptions={{
        click: handleSubmit(onSuccess),
        color: "green",
        title: translator("Enregistrer")
      }}
      buttonCancelOptions={{
        click: () => onClickClose(),
        color: "outlined",
        title: translator("Annuler")
      }}
      isLoading={isSaving}
    >
      <div className="arh-w-full ">
        <div className="arh-w-full arh-flex-col arh-items-center ">
          <span className="arh-font-poppins arh-text-[13px] arh-font-normal arh-text-black ">
            {translator(selectedSubject.evaluationType)}
          </span>
          <h3 className="arh-my-[1rem] arh-mb-0 arh-text-center arh-font-poppins arh-text-xl arh-font-semibold arh-text-black">
            {selectedSubject.name}
          </h3>
          <NotationPreview
            note={selectedSubject?.surveySubjectNotation?.note}
            evaluationFormat={selectedSubject.surveyFormat}
          />
        </div>

        <div className="arh-mt-[3rem] arh-w-full ">
          <div className="arh-mb-[1rem] arh-w-full arh-rounded-[7px] arh-border arh-border-solid arh-border-[#F2F4F7] arh-px-[1.5rem] arh-py-[1rem] ">
            <Controller
              control={control}
              name="nextObjective"
              render={({ field: { onChange, value } }) => (
                <TextareaForm
                  classLabel="arh-text-left arh-mb-[1rem] "
                  label={translator("Objectifs pour la prochaine évaluation")}
                  classInput="!arh-border-0 !arh-p-0 arh-min-h-[5rem]  "
                  placeholder={translator("Texte ici")}
                  value={value}
                  onChange={onChange}
                  readOnly={readOnly}
                />
              )}
            />
          </div>

          <div className="arh-mb-[1rem] arh-w-full arh-rounded-[7px] arh-border arh-border-solid arh-border-[#F2F4F7] arh-px-[1.5rem] arh-py-[1rem]">
            <Controller
              control={control}
              name="availableMeans"
              render={({ field: { onChange, value } }) => (
                <TextareaForm
                  classLabel="arh-text-left arh-mb-[1rem] "
                  label={translator("Moyens disponibles")}
                  classInput="!arh-border-0 !arh-p-0 arh-min-h-[5rem]  "
                  placeholder={translator("Texte ici")}
                  value={value}
                  onChange={onChange}
                  readOnly={readOnly}
                />
              )}
            />
          </div>

          <div className="arh-mb-[1rem] arh-w-full arh-rounded-[7px] arh-border arh-border-solid arh-border-[#F2F4F7] arh-px-[1.5rem] arh-py-[1rem]">
            <Controller
              control={control}
              name="mainObjective"
              render={({ field: { onChange, value } }) => (
                <TextareaForm
                  classLabel="arh-text-left arh-mb-[1rem] "
                  label={translator("Objectif général")}
                  classInput="!arh-border-0 !arh-p-0 arh-min-h-[5rem]  "
                  placeholder={translator("Texte ici")}
                  value={value}
                  onChange={onChange}
                  readOnly={readOnly}
                />
              )}
            />
          </div>
        </div>
      </div>
    </ModalIndex>
  );
}
